import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import { useAuthenticatedClient } from '@/api/queries'

export type TaxDataset = components['schemas']['QbTaxRateDatasetRead']
export type TaxDatasetDetail = components['schemas']['QbTaxRateDatasetDetailRead']
export type TaxDelivery = components['schemas']['QbTaxDeliveryRead']
export type TaxResolution = components['schemas']['QbTaxResolution']
export type TaxAddressLookup = components['schemas']['QbTaxAddressLookup']

export const taxRateKeys = {
  all: ['tax-rates'] as const,
  datasets: ['tax-rates', 'datasets'] as const,
  deliveries: ['tax-rates', 'deliveries'] as const,
  dataset: (id: string) => ['tax-rates', 'dataset', id] as const,
}

/** The API's `{"detail": {"code", "message"}}` errors, as a sentence for the screen. */
export function taxErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { detail?: unknown } | null)?.detail
  if (detail && typeof detail === 'object' && 'message' in detail) {
    const message = String((detail as { message: unknown }).message).trim()
    return /[.!?]$/.test(message) ? message : `${message}.`
  }
  return fallback
}

export function useTaxDatasets() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: taxRateKeys.datasets,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/quote-builder/tax-datasets')
      if (error) throw error
      return data
    },
  })
}

export function useTaxDeliveries() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: taxRateKeys.deliveries,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/quote-builder/tax-datasets/deliveries')
      if (error) throw error
      return data
    },
    retry: false,
  })
}

export function useTaxDataset(id: string | undefined) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: taxRateKeys.dataset(id ?? ''),
    enabled: Boolean(id),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/quote-builder/tax-datasets/{dataset_id}', {
        params: { path: { dataset_id: id ?? '' } },
      })
      if (error) throw error
      return data
    },
  })
}

export function useImportTaxDataset() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (sourceVersion: string) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/quote-builder/tax-datasets/import', {
        body: { source_version: sourceVersion },
      })
      if (error) throw error
      return data
    },
    onSuccess: (dataset) => {
      queryClient.setQueryData(taxRateKeys.dataset(dataset.id), dataset)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: taxRateKeys.all }),
  })
}

export function useActivateTaxDataset() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, expectedActiveId }: { id: string; expectedActiveId: string | null }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/quote-builder/tax-datasets/{dataset_id}/activate',
        { params: { path: { dataset_id: id } }, body: { expected_active_id: expectedActiveId } },
      )
      if (error) throw error
      return data
    },
    onSuccess: (dataset) => {
      // Optimistically show the new month as in use; the refetch confirms it.
      queryClient.setQueryData<TaxDataset[]>(taxRateKeys.datasets, (current) =>
        current?.map((item) =>
          item.id === dataset.id ? dataset : item.status === 'active' ? { ...item, status: 'retired' } : item,
        ),
      )
      queryClient.setQueryData(taxRateKeys.dataset(dataset.id), dataset)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: taxRateKeys.all }),
  })
}

export function useTaxLookup() {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (address: TaxAddressLookup) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/quote-builder/tax-datasets/lookup', {
        body: address,
      })
      if (error) throw error
      return data
    },
  })
}
